import { Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Suspense, useRef, useEffect, useState, lazy } from 'react';
import { getModule } from '../modules/registry';
import ModuleGuard from './ModuleGuard';
import ModuleLayout from './ModuleLayout';
import { matchPath } from 'react-router-dom';
import type { AppModuleManifest } from '../modules/types';
import type { ComponentType } from 'react';
import { TabProvider, useReportTabs } from '../modules/bauberichte/context/TabContext';
import { useStageDocumentTitle } from '../lib/useStage';
import { useAuth } from '@onvice/portal-shell-api';
import NoPermission from './NoPermission';
import ModuleErrorBoundary from './ModuleErrorBoundary';

function ModuleLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Lazy-import der TabBar (nur wenn Bauberichte-Modul geladen)
const TabBar = lazy(() => import('../modules/bauberichte/components/TabBar'));

function PersistentModuleContent({ manifest, basePath, relativePath }: {
  manifest: AppModuleManifest;
  basePath: string;
  relativePath: string;
}) {
  const { hasPermission } = useAuth();
  // Routen, die per `permission` gesperrt sind und der User die Permission NICHT hat,
  // werden komplett aus den persistenten Mounts entfernt. Sonst wuerde die Component
  // gemountet, ihre Effects laufen lassen und in Backend-403 reinrennen.
  const accessibleRoutes = manifest.routes.filter((r) => !r.permission || hasPermission(r.permission));
  const persistentRoutes = accessibleRoutes.filter((r) => r.navigation !== 'hidden');
  const dynamicRoutes = accessibleRoutes.filter((r) => r.navigation === 'hidden');
  const { tabs, activeTabId, openTab } = useReportTabs();

  // Pruefen ob aktueller Pfad eine dynamische Route ist
  let currentDynamicKey: string | null = null;
  let currentDynamicRoute: typeof dynamicRoutes[0] | null = null;
  let currentDynamicParams: Record<string, string> = {};

  for (const route of dynamicRoutes) {
    const match = matchPath({ path: route.path, end: true }, relativePath);
    if (match) {
      currentDynamicParams = (match.params || {}) as Record<string, string>;
      currentDynamicKey = Object.values(currentDynamicParams).join('/') || relativePath;
      currentDynamicRoute = route;
      break;
    }
  }
  const isOnDynamicRoute = !!currentDynamicKey;

  // Tab oeffnen wenn auf dynamischer Route — als Effect nach dem Commit,
  // damit forceCloseTab + navigate erst durchkommen koennen ohne Auto-Reopen.
  useEffect(() => {
    if (currentDynamicKey && !tabs.some((t) => t.id === currentDynamicKey)) {
      openTab(currentDynamicKey);
    }
  }, [currentDynamicKey, tabs, openTab]);

  // Alle geoeffneten Tabs als Komponenten-Instanzen tracken
  const tabComponentsRef = useRef<Map<string, {
    Component: ComponentType<Record<string, string>>;
    params: Record<string, string>;
  }>>(new Map());

  // Aktuelle dynamische Route in die Tab-Map aufnehmen
  if (currentDynamicKey && currentDynamicRoute && !tabComponentsRef.current.has(currentDynamicKey)) {
    tabComponentsRef.current.set(currentDynamicKey, {
      Component: currentDynamicRoute.component as ComponentType<Record<string, string>>,
      params: currentDynamicParams,
    });
  }

  // Geschlossene Tabs aus der Map entfernen
  for (const key of tabComponentsRef.current.keys()) {
    if (!tabs.some((t) => t.id === key)) {
      tabComponentsRef.current.delete(key);
    }
  }

  return (
    <>
      {/* Tab-Leiste — sichtbar wenn Tabs offen sind */}
      {tabs.length > 0 && (
        <Suspense fallback={null}>
          <TabBar />
        </Suspense>
      )}

      <Suspense fallback={<ModuleLoader />}>
        {/* Persistente Seiten */}
        {persistentRoutes.map((route) => {
          const isActive = !isOnDynamicRoute && !!matchPath(
            { path: route.path, end: true },
            relativePath
          );
          const Component = route.component;
          return (
            <div key={route.path} style={{ display: isActive ? undefined : 'none' }}>
              <Component />
            </div>
          );
        })}

        {/* Offene Report-Tabs — alle gemountet, nur aktiver sichtbar */}
        {Array.from(tabComponentsRef.current.entries()).map(([key, { Component, params }]) => {
          const isActive = isOnDynamicRoute && currentDynamicKey === key;
          return (
            <div key={key} style={{ display: isActive ? undefined : 'none' }}>
              <Component {...params} />
            </div>
          );
        })}
      </Suspense>
    </>
  );
}

export default function ModuleRouter() {
  const { moduleSlug, '*': splat } = useParams<{ moduleSlug: string; '*': string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // rf9 (2026-08-17): Race-Fix fuer dynamisch registrierte Federation-Module.
  // Beim ersten Render (Portal-Boot) sind statische Remotes registriert, aber
  // dynamische (aus /api/portal/modules/available) noch nicht — initDynamicRemotes
  // laeuft erst nach isAuthenticated=true in AuthEffects. `getModule(slug)` wuerde
  // dann `undefined` liefern → sofort `Navigate to="/"` → HomePage-Redirect zu
  // /dashboard. Wenn Faktura kurz danach registriert wird, ist die URL schon weg.
  // Fix: lauschen auf `federation-module-registered`-Event, State-Bump, Re-Read.
  // Gilt Race nur bis das Modul geladen ist — danach ist der Hook harmlos.
  const [regTick, setRegTick] = useState(0);
  const [waitedTooLong, setWaitedTooLong] = useState(false);
  useEffect(() => {
    if (!moduleSlug) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.slug === moduleSlug) setRegTick((t) => t + 1);
    };
    window.addEventListener('federation-module-registered', handler);
    // Timeout: nach 6s als "existiert nicht" behandeln — Fallback wie vorher
    // (Navigate to '/'). Sonst rotiert der Spinner ewig bei falscher URL.
    const t = setTimeout(() => setWaitedTooLong(true), 6000);
    return () => {
      window.removeEventListener('federation-module-registered', handler);
      clearTimeout(t);
    };
  }, [moduleSlug]);
  void regTick;

  const manifest = moduleSlug ? getModule(moduleSlug) : undefined;
  // Browser-Tab-Titel auf den Modul-Namen setzen — pro Tab unabhaengig,
  // damit man mehrere Apps in parallelen Fenstern auseinanderhalten kann.
  useStageDocumentTitle(manifest ? `${manifest.name} · onvice` : 'onvice Portal');

  if (!manifest) {
    if (waitedTooLong) {
      // Modul-Slug existiert nicht (falsche URL, offline Container, RBAC) —
      // wie vorher: zur Startseite.
      return <Navigate to="/" replace />;
    }
    // rf9: Waehrend dynamische Remotes noch laden — Loader zeigen. Nach
    // federation-module-registered-Event triggert regTick + Re-Render und
    // getModule liefert das Manifest.
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const basePath = `/m/${manifest.slug}`;
  const relativePath = '/' + (splat || '');

  if (manifest.persistent) {
    return (
      <TabProvider onNavigate={navigate}>
        <ModuleGuard moduleSlug={manifest.slug}>
          <ModuleLayout manifest={manifest}>
            <ModuleErrorBoundary label={manifest.name} scope="module">
              <PersistentModuleContent
                manifest={manifest}
                basePath={basePath}
                relativePath={relativePath}
              />
            </ModuleErrorBoundary>
          </ModuleLayout>
        </ModuleGuard>
      </TabProvider>
    );
  }

  // Standard mode
  return (
    <ModuleGuard moduleSlug={manifest.slug}>
      <ModuleLayout manifest={manifest}>
        <ModuleErrorBoundary label={manifest.name} scope="module">
          <Suspense fallback={<ModuleLoader />}>
            <Routes>
              {manifest.routes.map((route) => {
                const allowed = !route.permission || hasPermission(route.permission);
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={allowed ? <route.component /> : <NoPermission moduleName={manifest.name} />}
                  />
                );
              })}
              <Route path="*" element={<Navigate to={basePath} replace />} />
            </Routes>
          </Suspense>
        </ModuleErrorBoundary>
      </ModuleLayout>
    </ModuleGuard>
  );
}
